import React, { useState, useEffect, useRef } from 'react';
import {
  Table, Tag, Button, Space, message, Alert, Modal, Select, DatePicker,
  Upload, Card, List, Progress, Switch, Input, Divider, Collapse
} from 'antd';
import {
  HomeOutlined, UploadOutlined, DeleteOutlined, PlusOutlined,
  InboxOutlined, FileTextOutlined, CheckCircleOutlined,
  WarningOutlined, ExclamationCircleOutlined, EyeOutlined,
  FilePdfOutlined, PaperClipOutlined, HolderOutlined
} from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../../utils/auth';
import dayjs from 'dayjs';
import HotelFormModal from '../../components/HotelFormModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';
const { Option } = Select;

const StepHotelAssignment = ({ tourists, tour, completeness, onRefresh }) => {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [selectedTouristIds, setSelectedTouristIds] = useState([]);
  const [batchHotels, setBatchHotels] = useState([{ hotelId: null, checkInDate: null, checkOutDate: null, bookingNumber: '' }]);
  const [saving, setSaving] = useState(false);

  // 内联新建酒店（复用酒店库管理的完整表单）
  const [createHotelVisible, setCreateHotelVisible] = useState(false);
  const [createHotelForRow, setCreateHotelForRow] = useState(null);
  // 批量分配弹窗中展开的酒店卡片（key = 行的稳定 _key）
  const [expandedHotelKeys, setExpandedHotelKeys] = useState([]);
  // 拖拽排序状态
  const [dragKey, setDragKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [dragEnabled, setDragEnabled] = useState(false); // 仅按住手柄时可拖
  const hotelRowSeq = useRef(0);
  const newRowKey = () => `hr${++hotelRowSeq.current}`;

  // 批量匹配酒店PDF
  const [batchUploadModalVisible, setBatchUploadModalVisible] = useState(false);
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchUploading, setBatchUploading] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [batchProgress, setBatchProgress] = useState(0);
  const [batchCurrentFile, setBatchCurrentFile] = useState('');
  const [batchCompletedCount, setBatchCompletedCount] = useState(0);
  const [batchMatchName, setBatchMatchName] = useState(true);

  const touristDetails = completeness?.tourists || [];
  const stats = completeness?.tour?.touristStats;

  useEffect(() => {
    loadHotels();
  }, []);

  const loadHotels = async () => {
    try {
      // 不按产品过滤，加载全部启用酒店供搜索
      const res = await axios.get(
        `${API_BASE}/hotels?isActive=true`,
        { headers: AuthService.getAuthHeaders() }
      );
      if (res.data.success) {
        setHotels(res.data.data || []);
      }
    } catch (err) {
      console.error('加载酒店列表失败:', err);
    }
  };

  // 内联新建酒店保存成功：刷新列表并选入触发行
  const handleHotelCreated = async (hotel) => {
    setCreateHotelVisible(false);
    await loadHotels();
    if (hotel?.id != null && createHotelForRow != null) {
      updateBatchHotel(createHotelForRow, 'hotelId', hotel.id);
    }
    setCreateHotelForRow(null);
  };

  const handleBatchAssign = async () => {
    if (selectedTouristIds.length === 0) {
      message.warning('请选择游客');
      return;
    }
    const validHotels = batchHotels.filter(h => h.hotelId);
    if (validHotels.length === 0) {
      message.warning('请至少选择一个酒店');
      return;
    }

    try {
      setSaving(true);
      await axios.post(
        `${API_BASE}/tourist-assignments/batch-hotels`,
        {
          touristIds: selectedTouristIds,
          hotels: validHotels.map((h, i) => ({
            hotelId: h.hotelId,
            checkInDate: h.checkInDate,
            checkOutDate: h.checkOutDate,
            bookingNumber: h.bookingNumber || null,
            sortOrder: i,
          })),
        },
        { headers: AuthService.getAuthHeaders() }
      );
      message.success('批量分配成功');
      setBatchModalVisible(false);
      setSelectedTouristIds([]);
      setBatchHotels([{ hotelId: null, checkInDate: null, checkOutDate: null, bookingNumber: '' }]);
      onRefresh();
    } catch (err) {
      message.error('批量分配失败: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const updateBatchHotel = (index, field, value) => {
    setBatchHotels(prev => prev.map((h, i) => i === index ? { ...h, [field]: value } : h));
  };

  const addBatchHotelRow = () => {
    // 新酒店的入住日期自动接上一家的退房日期
    const last = batchHotels[batchHotels.length - 1];
    const key = newRowKey();
    setBatchHotels(prev => [...prev, { _key: key, hotelId: null, checkInDate: last?.checkOutDate || null, checkOutDate: null, bookingNumber: '' }]);
    // 只展开新增的这一家，其余折叠
    setExpandedHotelKeys([key]);
  };

  const removeBatchHotelRow = (index) => {
    const removed = batchHotels[index];
    setBatchHotels(prev => prev.filter((_, i) => i !== index));
    if (removed?._key) {
      setExpandedHotelKeys(prev => prev.filter(k => k !== removed._key));
    }
  };

  // 拖拽排序：把 dragKey 的行移动到 overKey 的位置
  const reorderBatchHotels = (fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    setBatchHotels(prev => {
      const from = prev.findIndex(h => h._key === fromKey);
      const to = prev.findIndex(h => h._key === toKey);
      if (from < 0 || to < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const handleClearHotel = async (touristId) => {
    try {
      await axios.put(
        `${API_BASE}/tourist-assignments/${touristId}/hotels`,
        { hotels: [] },
        { headers: AuthService.getAuthHeaders() }
      );
      message.success('已清除酒店分配');
      onRefresh();
    } catch (err) {
      message.error('清除失败: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUploadDoc = async (touristId, file) => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', 'hotel');
    // 不传 documentName，后端会自动使用原始文件名

    try {
      await axios.post(
        `${API_BASE}/tourist-documents/tourist/${touristId}`,
        formData,
        {
          headers: {
            ...AuthService.getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      message.success('上传成功');
      onRefresh();
    } catch (err) {
      message.error('上传失败: ' + (err.response?.data?.message || err.message));
    }
    return false;
  };

  // 批量匹配酒店PDF
  const handleBatchFileChange = (info) => {
    const fileList = info.fileList
      .filter(file => file.originFileObj && file.type === 'application/pdf')
      .map(file => file.originFileObj);
    setBatchFiles(fileList);
  };

  const handleBatchUpload = async () => {
    if (batchFiles.length === 0) {
      message.error('请选择要上传的酒店PDF文件');
      return;
    }

    setBatchUploading(true);
    setBatchResult(null);
    setBatchProgress(0);
    setBatchCurrentFile('');
    setBatchCompletedCount(0);

    const totalFiles = batchFiles.length;
    const results = { success: [], notMatched: [], errors: [] };
    const departureDate = tour?.departureDate ? dayjs(tour.departureDate).format('YYYY-MM-DD') : '';

    try {
      for (let i = 0; i < totalFiles; i++) {
        const file = batchFiles[i];
        setBatchCurrentFile(file.name);
        setBatchProgress(Math.round((i / totalFiles) * 100));

        const formData = new FormData();
        formData.append('departureDate', departureDate);
        formData.append('files', file);
        if (!batchMatchName) {
          formData.append('matchMode', 'all');
        }

        try {
          const response = await axios.post(
            `${API_BASE}/tourist-documents/batch-upload-hotels`,
            formData,
            {
              headers: {
                ...AuthService.getAuthHeaders(),
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          if (response.data.success) {
            const data = response.data.data;
            if (data.success) results.success.push(...data.success);
            if (data.notMatched) results.notMatched.push(...data.notMatched);
            if (data.errors) results.errors.push(...data.errors);
          }
        } catch (err) {
          results.errors.push({
            fileName: file.name,
            error: err.response?.data?.error || '上传失败'
          });
        }

        setBatchCompletedCount(i + 1);
      }

      setBatchProgress(100);
      setBatchResult(results);

      const successCount = results.success.length;
      const notMatchedCount = results.notMatched.length;
      const errorCount = results.errors.length;
      message.success(`上传完成: ${successCount} 成功, ${notMatchedCount} 未匹配, ${errorCount} 失败`);

      onRefresh();
    } catch (error) {
      message.error(error.response?.data?.error || '批量上传失败');
    } finally {
      setBatchUploading(false);
      setBatchProgress(0);
      setBatchCurrentFile('');
      setBatchCompletedCount(0);
    }
  };

  const handleViewDoc = async (docId) => {
    try {
      const res = await axios.get(
        `${API_BASE}/tourist-documents/${docId}/url`,
        { headers: AuthService.getAuthHeaders() }
      );
      if (res.data.success && res.data.data?.fileUrl) {
        window.open(res.data.data.fileUrl, '_blank');
      } else {
        message.error('获取文件链接失败');
      }
    } catch (err) {
      message.error('获取文件链接失败');
    }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await axios.delete(
        `${API_BASE}/tourist-documents/${docId}`,
        { headers: AuthService.getAuthHeaders() }
      );
      message.success('已删除');
      onRefresh();
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleBatchUploadModalClose = () => {
    setBatchUploadModalVisible(false);
    setBatchFiles([]);
    setBatchResult(null);
    setBatchMatchName(true);
    onRefresh();
  };

  const rowSelection = {
    selectedRowKeys: selectedTouristIds,
    onChange: (keys) => setSelectedTouristIds(keys),
  };

  // 多规格团：按规格分组，供"一键选中该规格全部游客 → 批量分配对应酒店"
  const SPEC_LABEL = { with_tour: 'Plus款', no_tour: '基础款', '2star': '二钻', '3star': '三钻', '4star': '四钻', '5star': '五钻' };
  const specGroups = (() => {
    const m = new Map();
    (touristDetails || []).forEach((t) => {
      [t.packageType, t.hotelTier].filter(Boolean).forEach((k) => {
        if (!m.has(k)) m.set(k, []);
        m.get(k).push(t.id);
      });
    });
    return [...m.entries()].map(([k, ids]) => ({ key: k, label: SPEC_LABEL[k] || k, ids }));
  })();

  const renderSpec = (r) => {
    const P = { with_tour: ['Plus款', 'blue'], no_tour: ['基础款', 'default'] }[r.packageType];
    const T = { '2star': ['二钻', 'green'], '3star': ['三钻', 'gold'], '4star': ['四钻', 'volcano'], '5star': ['五钻', 'red'] }[r.hotelTier];
    const tags = [];
    if (P) tags.push(<Tag key="p" color={P[1]} style={{ margin: 0 }}>{P[0]}</Tag>);
    if (T) tags.push(<Tag key="t" color={T[1]} style={{ margin: 0 }}>{T[0]}</Tag>);
    if (!tags.length) return <span style={{ color: '#bbb', fontSize: 12 }}>默认规格</span>;
    return <Space size={4} wrap>{tags}</Space>;
  };

  const columns = [
    {
      title: '护照姓名',
      dataIndex: 'passportName',
      key: 'passportName',
      width: 150,
      fixed: 'left',
      render: (val) => val || '-',
    },
    {
      title: '规格',
      key: 'spec',
      width: 130,
      render: (_, r) => renderSpec(r),
    },
    {
      title: '酒店分配',
      key: 'hotels',
      width: 240,
      render: (_, record) => {
        if (!record.hotelAssigned) {
          return <Tag color="warning">未分配</Tag>;
        }
        // 只上传了酒店 PDF、没有分配结构化酒店：视为已分配，通知书显示"详情见PDF附件"
        if (!record.hotels?.length) {
          return <Tag color="green">已上传PDF（详情见附件）</Tag>;
        }
        return (
          <Space direction="vertical" size={2}>
            {record.hotels.map((h, i) => (
              <div key={i}>
                <Tag color="blue">{h.hotelName} ({h.roomType || '标准'})</Tag>
                {h.bookingNumber && (
                  <div style={{ fontSize: 11, color: '#666', marginTop: 2, fontFamily: 'monospace' }}>
                    预订号：{h.bookingNumber}
                  </div>
                )}
              </div>
            ))}
          </Space>
        );
      },
    },
    {
      title: '入住/退房',
      key: 'dates',
      width: 180,
      render: (_, record) => {
        if (!record.hotels?.length) return '-';
        return (
          <Space direction="vertical" size={2}>
            {record.hotels.map((h, i) => (
              <span key={i} style={{ fontSize: '12px' }}>
                {h.checkInDate || '?'} ~ {h.checkOutDate || '?'}
              </span>
            ))}
          </Space>
        );
      },
    },
    {
      title: '酒店PDF',
      key: 'hotelBooking',
      width: 220,
      render: (_, record) => {
        const docs = record.hotelDocuments || [];
        return (
          <div>
            {docs.length > 0 ? (
              <div style={{ marginBottom: 4 }}>
                {docs.map((doc) => (
                  <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2, fontSize: 12 }}>
                    <FilePdfOutlined style={{ color: '#ff4d4f', flexShrink: 0 }} />
                    <span
                      style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1890ff', cursor: 'pointer', maxWidth: 120 }}
                      title={doc.documentName}
                      onClick={() => handleViewDoc(doc.id)}
                    >
                      {doc.documentName}
                    </span>
                    <Button type="link" size="small" icon={<EyeOutlined />} style={{ padding: 0, height: 'auto', minWidth: 0 }} onClick={() => handleViewDoc(doc.id)} />
                    <Button type="link" size="small" danger icon={<DeleteOutlined />} style={{ padding: 0, height: 'auto', minWidth: 0 }} onClick={() => handleDeleteDoc(doc.id)} />
                  </div>
                ))}
              </div>
            ) : (
              <Tag color="default" style={{ marginBottom: 4 }}>未上传</Tag>
            )}
            <Upload
              beforeUpload={(file) => handleUploadDoc(record.id, file)}
              showUploadList={false}
              accept=".pdf,.jpg,.jpeg,.png"
              multiple
            >
              <Button size="small" icon={<UploadOutlined />} type={docs.length === 0 ? 'primary' : 'default'}>
                {docs.length === 0 ? '上传' : '追加'}
              </Button>
            </Upload>
          </div>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record) => (
        record.hotels?.length > 0 && (
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleClearHotel(record.id)}
          >
            清除
          </Button>
        )
      ),
    },
  ];

  if (!tour?.destinationId) {
    return <Alert type="warning" message="请先在团组中设置目的地后再分配酒店" showIcon />;
  }

  return (
    <div>
      <div className="stat-cards">
        <div className={`stat-card ${stats?.hotelAssigned === stats?.total ? 'success' : 'warning'}`}>
          <div className="stat-value">{stats?.hotelAssigned || 0}/{stats?.total || 0}</div>
          <div className="stat-label">已分配酒店</div>
        </div>
      </div>

      {specGroups.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <span style={{ fontSize: 12.5, color: '#666', marginRight: 6 }}>按规格一键选择：</span>
          <Space size={6} wrap>
            <Button size="small" onClick={() => setSelectedTouristIds((touristDetails || []).map((t) => t.id))}>
              全部 {(touristDetails || []).length}
            </Button>
            {specGroups.map((g) => (
              <Button key={g.key} size="small" onClick={() => setSelectedTouristIds(g.ids)}>{g.label} {g.ids.length}</Button>
            ))}
          </Space>
          <span style={{ fontSize: 11.5, color: '#999', marginLeft: 8 }}>选中一档 → 点右下「批量分配酒店」分配该规格酒店</span>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button
            type="primary"
            icon={<HomeOutlined />}
            onClick={() => {
              // 打开弹窗时，从已选游客的现有分配中找一个非空的预填进表单（多人共用同一份分配）
              const selectedTourists = (touristDetails || []).filter(t => selectedTouristIds.includes(t.id));
              const sample = selectedTourists.find(t => Array.isArray(t.hotels) && t.hotels.length > 0);
              let rows;
              if (sample) {
                rows = sample.hotels.map(h => ({
                  _key: newRowKey(),
                  hotelId: h.hotelId || null,
                  checkInDate: h.checkInDate || null,
                  checkOutDate: h.checkOutDate || null,
                  bookingNumber: h.bookingNumber || ''
                }));
              } else {
                rows = [{ _key: newRowKey(), hotelId: null, checkInDate: null, checkOutDate: null, bookingNumber: '' }];
              }
              setBatchHotels(rows);
              setExpandedHotelKeys(rows.length === 1 ? [rows[0]._key] : []);
              setBatchModalVisible(true);
            }}
            disabled={selectedTouristIds.length === 0}
          >
            批量分配酒店 {selectedTouristIds.length > 0 && `(${selectedTouristIds.length}人)`}
          </Button>
          <Button
            icon={<FileTextOutlined />}
            onClick={() => setBatchUploadModalVisible(true)}
          >
            批量匹配酒店PDF
          </Button>
        </Space>
      </div>

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={touristDetails}
        rowKey="id"
        size="small"
        scroll={{ x: 800 }}
        pagination={false}
      />

      {/* 批量分配酒店 Modal */}
      <Modal
        title="批量分配酒店"
        open={batchModalVisible}
        onCancel={() => setBatchModalVisible(false)}
        onOk={handleBatchAssign}
        confirmLoading={saving}
        width={600}
      >
        <Alert
          type="info"
          message={`将为 ${selectedTouristIds.length} 位游客分配酒店（已有分配将被覆盖）`}
          style={{ marginBottom: 16 }}
          showIcon
        />
        <div style={{ marginBottom: 12 }}>
          {batchHotels.map((item, index) => {
            const hotelInfo = hotels.find(h => h.id === item.hotelId);
            const isDragging = dragKey === item._key;
            const isDragOver = dragOverKey === item._key && dragKey !== item._key;
            return (
              <div
                key={item._key}
                draggable={dragEnabled}
                onDragStart={(e) => {
                  setDragKey(item._key);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragOverKey !== item._key) setDragOverKey(item._key);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  reorderBatchHotels(dragKey, item._key);
                  setDragKey(null);
                  setDragOverKey(null);
                  setDragEnabled(false);
                }}
                onDragEnd={() => {
                  setDragKey(null);
                  setDragOverKey(null);
                  setDragEnabled(false);
                }}
                style={{
                  marginBottom: 8,
                  opacity: isDragging ? 0.4 : 1,
                  borderTop: isDragOver ? '2px solid #1677ff' : '2px solid transparent',
                  transition: 'border-color 0.15s',
                }}
              >
                <Collapse
                  activeKey={expandedHotelKeys.includes(item._key) ? [item._key] : []}
                  onChange={(keys) => {
                    const open = (Array.isArray(keys) ? keys : [keys]).length > 0;
                    setExpandedHotelKeys(prev => open
                      ? [...prev.filter(k => k !== item._key), item._key]
                      : prev.filter(k => k !== item._key));
                  }}
                  items={[{
              key: item._key,
              label: (
                <Space size={8} wrap>
                  <HolderOutlined
                    style={{ cursor: 'grab', color: '#999' }}
                    title="按住拖拽排序"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => { e.stopPropagation(); setDragEnabled(true); }}
                    onMouseUp={() => setDragEnabled(false)}
                  />
                  <b>酒店 {index + 1}</b>
                  {hotelInfo && <span style={{ color: '#1677ff' }}>{hotelInfo.name}</span>}
                  {(item.checkInDate || item.checkOutDate) && (
                    <span style={{ color: '#999', fontSize: 12 }}>
                      {item.checkInDate || '?'} ~ {item.checkOutDate || '?'}
                    </span>
                  )}
                </Space>
              ),
              extra: (
                <Space size={0} onClick={(e) => e.stopPropagation()}>
                  {batchHotels.length > 1 && (
                    <Button
                      type="text" danger size="small" icon={<DeleteOutlined />}
                      title="删除"
                      onClick={() => removeBatchHotelRow(index)}
                    />
                  )}
                </Space>
              ),
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <div>
                    <div style={{ marginBottom: 4 }}>选择酒店：</div>
                    <Select
                      placeholder="搜索酒店名称..."
                      value={item.hotelId}
                      onChange={v => updateBatchHotel(index, 'hotelId', v)}
                      style={{ width: '100%' }}
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                      }
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          <Divider style={{ margin: '6px 0' }} />
                          <Button
                            type="link"
                            size="small"
                            icon={<PlusOutlined />}
                            style={{ paddingLeft: 8 }}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { setCreateHotelForRow(index); setCreateHotelVisible(true); }}
                          >
                            新建酒店
                          </Button>
                        </>
                      )}
                    >
                      {hotels.map(h => (
                        <Option key={h.id} value={h.id}>
                          {h.name}{h.nameEn ? ` / ${h.nameEn}` : ''}{h.starLevel ? ` · ${h.starLevel}星` : ''}{h.city ? ` · ${h.city}` : ''}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  <Space style={{ width: '100%' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: 4 }}>入住日期：</div>
                      <DatePicker
                        style={{ width: '100%' }}
                        value={item.checkInDate ? dayjs(item.checkInDate) : null}
                        defaultPickerValue={tour?.departureDate ? dayjs(tour.departureDate) : undefined}
                        onChange={(d, ds) => updateBatchHotel(index, 'checkInDate', ds)}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: 4 }}>退房日期：</div>
                      <DatePicker
                        style={{ width: '100%' }}
                        value={item.checkOutDate ? dayjs(item.checkOutDate) : null}
                        defaultPickerValue={item.checkInDate ? dayjs(item.checkInDate) : (tour?.departureDate ? dayjs(tour.departureDate) : undefined)}
                        onChange={(d, ds) => updateBatchHotel(index, 'checkOutDate', ds)}
                      />
                    </div>
                  </Space>
                  <div>
                    <div style={{ marginBottom: 4 }}>预订号（可选，团内多人可共用）：</div>
                    <Input
                      placeholder="酒店预订确认号"
                      value={item.bookingNumber || ''}
                      onChange={(e) => updateBatchHotel(index, 'bookingNumber', e.target.value)}
                    />
                  </div>
                </Space>
              ),
                  }]}
                />
              </div>
            );
          })}
        </div>
        <Button type="dashed" block icon={<PlusOutlined />} onClick={addBatchHotelRow}>
          添加酒店
        </Button>
      </Modal>

      {/* 批量匹配酒店PDF Modal */}
      <Modal
        title="批量匹配酒店PDF"
        open={batchUploadModalVisible}
        onCancel={handleBatchUploadModalClose}
        footer={null}
        width={640}
        destroyOnClose
      >
        {batchUploading ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ marginBottom: 8, fontSize: 16, fontWeight: 500 }}>
              正在上传酒店文件 ({batchCompletedCount}/{batchFiles.length})
            </div>
            <Progress
              percent={batchProgress}
              status="active"
              strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
              style={{ maxWidth: 400, margin: '0 auto' }}
            />
            {batchCurrentFile && (
              <div style={{ marginTop: 12, color: '#1890ff', fontFamily: 'monospace', fontSize: 13 }}>
                {batchCurrentFile}
              </div>
            )}
            <div style={{ marginTop: 12, color: '#999', fontSize: 12 }}>
              请勿关闭此窗口，正在逐个上传并通过OCR匹配游客...
            </div>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span>匹配姓名：</span>
              <Switch checked={batchMatchName} onChange={setBatchMatchName} />
              <span style={{ color: '#999', fontSize: 12 }}>
                {batchMatchName ? '通过OCR识别PDF中旅客姓名匹配' : '不匹配，直接发给所有游客'}
              </span>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Upload.Dragger
                accept=".pdf"
                multiple
                beforeUpload={() => false}
                onChange={handleBatchFileChange}
                fileList={batchFiles.map((file, index) => ({
                  uid: index,
                  name: file.name,
                  status: 'done',
                  originFileObj: file,
                  type: file.type
                }))}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">点击或拖拽酒店PDF文件到此区域</p>
                <p className="ant-upload-hint">
                  支持多文件上传{batchMatchName ? '，系统将通过OCR识别PDF中的旅客姓名自动匹配游客' : '，将发给所有游客'}
                </p>
              </Upload.Dragger>
            </div>

            {batchFiles.length > 0 && (
              <div style={{ color: '#1890ff', marginBottom: 16 }}>
                已选择 {batchFiles.length} 个文件
              </div>
            )}

            <div style={{ textAlign: 'right', marginBottom: batchResult ? 16 : 0 }}>
              <Space>
                <Button onClick={handleBatchUploadModalClose}>关闭</Button>
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={handleBatchUpload}
                  disabled={batchFiles.length === 0}
                >
                  开始上传
                </Button>
              </Space>
            </div>

            {batchResult && (
              <div>
                <div style={{ marginBottom: 12 }}>
                  <Space size="large">
                    <Tag color="success" style={{ padding: '4px 12px', fontSize: 14 }}>
                      <CheckCircleOutlined /> 成功: {batchResult.success?.length || 0}
                    </Tag>
                    <Tag color="warning" style={{ padding: '4px 12px', fontSize: 14 }}>
                      <WarningOutlined /> 未匹配: {batchResult.notMatched?.length || 0}
                    </Tag>
                    <Tag color="error" style={{ padding: '4px 12px', fontSize: 14 }}>
                      <ExclamationCircleOutlined /> 失败: {batchResult.errors?.length || 0}
                    </Tag>
                  </Space>
                </div>

                {batchResult.success?.length > 0 && (
                  <Card size="small" title="匹配成功" style={{ marginBottom: 8 }}>
                    <List
                      size="small"
                      dataSource={batchResult.success}
                      renderItem={(item) => (
                        <List.Item>
                          <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                          <span>{item.fileName}</span>
                          <span style={{ marginLeft: 8, color: '#666' }}>&rarr; {item.touristName}</span>
                          {item.travelerName && (
                            <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>
                              (识别: {item.travelerName})
                            </span>
                          )}
                          {item.hotelAssigned && (
                            <Tag color="blue" style={{ marginLeft: 8 }}>{item.hotelName}</Tag>
                          )}
                        </List.Item>
                      )}
                      style={{ maxHeight: 150, overflow: 'auto' }}
                    />
                  </Card>
                )}

                {batchResult.notMatched?.length > 0 && (
                  <Card size="small" title="未匹配" style={{ marginBottom: 8 }}>
                    <List
                      size="small"
                      dataSource={batchResult.notMatched}
                      renderItem={(item) => (
                        <List.Item>
                          <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
                          <span>{item.fileName}</span>
                          <span style={{ marginLeft: 8, color: '#999' }}>
                            {item.travelers && `(识别: ${item.travelers.join(', ')})`}
                            {' '}{item.reason}
                          </span>
                        </List.Item>
                      )}
                      style={{ maxHeight: 120, overflow: 'auto' }}
                    />
                  </Card>
                )}

                {batchResult.errors?.length > 0 && (
                  <Card size="small" title="上传失败" style={{ marginBottom: 8 }}>
                    <List
                      size="small"
                      dataSource={batchResult.errors}
                      renderItem={(item) => (
                        <List.Item>
                          <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                          <span>{item.fileName}</span>
                          <span style={{ marginLeft: 8, color: '#ff4d4f' }}>{item.error}</span>
                        </List.Item>
                      )}
                      style={{ maxHeight: 100, overflow: 'auto' }}
                    />
                  </Card>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 内联新建酒店（和酒店库管理同一套表单） */}
      <HotelFormModal
        open={createHotelVisible}
        editingHotel={null}
        onCancel={() => { setCreateHotelVisible(false); setCreateHotelForRow(null); }}
        onSaved={handleHotelCreated}
      />
    </div>
  );
};

export default StepHotelAssignment;
